import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  useToast,
} from '@chakra-ui/react';

import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import { disableAutorenew } from '../api';
import { useGlobalContext } from '../state/GlobalContext';
import { useLoadingToast } from '../../utils/useLoadingToast';
import { startMomentsApplicationsRefetchBackoff } from '../../utils/momentsRefetchBackoff';

interface BaseMutationQuery {
  customerId?: string;
  subscriptionId?: string;
  authToken?: string;
}

const ConfirmPrepaidCancellationModal: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { data, loadPreviousFlowStep, onClose } = useModalManagerContext();
  const { authToken, customerId, subscriptionId, shopHasActiveMoments } =
    useGlobalContext();
  const queryClient = useQueryClient();

  const { addLoadingToast, removeLoadingToast } = useLoadingToast();

  const callCancelSubscription = ({
    customerId,
    subscriptionId,
    authToken,
  }: BaseMutationQuery) => {
    setLoading(true);
    addLoadingToast();
    return disableAutorenew(
      customerId,
      subscriptionId,
      authToken,
      data.reason,
      data.details,
    );
  };

  const onCancelSubscriptionMutationSuccess = async () => {
    const previousSubscriptionValue = queryClient.getQueryData<Subscription>([
      'subscription',
      customerId,
      subscriptionId,
    ]);
    removeLoadingToast();

    queryClient.setQueryData(['subscription', customerId, subscriptionId], {
      ...previousSubscriptionValue,
      ItemTypeSubscriptionStatusNextBillingDate: 'SC#CANCELLED#',
      BillingPolicyMaxCycles: 1,
      CancelDate: new Date(),
      CancelReason: 'SHOPPER_CANCELLED',
    });
    if (shopHasActiveMoments) {
      startMomentsApplicationsRefetchBackoff({
        queryClient,
        subscriptionId,
      });
    }
    onClose(t('cancelModal.confirmPrepaid.success'));
  };

  const onCancelSubscriptionMutationError = () => {
    removeLoadingToast();

    toast({
      isClosable: true,
      position: 'top',
      status: 'error',
      title: t('subscription.cancel_error'),
      variant: 'error',
    });
  };

  const onCancelSubscriptionMutationSettled = () => {
    removeLoadingToast();

    setLoading(false);
  };

  const cancelSubscriptionMutation = useMutation(callCancelSubscription, {
    onSuccess: onCancelSubscriptionMutationSuccess,
    onError: onCancelSubscriptionMutationError,
    onSettled: onCancelSubscriptionMutationSettled,
  });

  const handleConfirmCancellationClick = () => {
    cancelSubscriptionMutation.mutate({
      customerId,
      subscriptionId,
      authToken,
    });
  };

  return (
    <>
      <ModalHeader>{t('cancelModal.confirmPrepaid.modalTitle')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Box>
          <Text>{t('cancelModal.confirmPrepaid.description')}</Text>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button mr="4" onClick={loadPreviousFlowStep} variant="outlinedBig">
          {t('cancelModal.back')}
        </Button>
        <Button
          isLoading={loading}
          onClick={handleConfirmCancellationClick}
          variant="dangerBig"
        >
          {t('cancelModal.confirmPrepaid.button')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ConfirmPrepaidCancellationModal;
